// src/components/files/MoveDialog.tsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Collapse,
  alpha,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import HomeIcon from '@mui/icons-material/Home';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import { useTranslation } from 'react-i18next';
import { useFileManager } from '../../contexts/FileManagerContext';
import * as fileService from '../../services/fileService';
import { FolderTree } from '../../types/files';

interface MoveDialogProps {
  open: boolean;
  onClose: () => void;
  itemType: 'folder' | 'document';
  itemId: number;
  itemName: string;
  currentFolderId: number | null;
}

interface FolderTreeItemProps {
  folder: FolderTree;
  level: number;
  selectedId: number | null;
  expandedIds: Set<number | null>;
  disabledId?: number;
  onSelect: (id: number | null) => void;
  onToggle: (id: number | null) => void;
}

const FolderTreeItem: React.FC<FolderTreeItemProps> = ({
  folder,
  level,
  selectedId,
  expandedIds,
  disabledId,
  onSelect,
  onToggle,
}) => {
  const isExpanded = expandedIds.has(folder.id);
  const isSelected = selectedId === folder.id;
  const isDisabled = folder.id === disabledId;
  const hasChildren = folder.children && folder.children.length > 0;

  return (
    <>
      <ListItemButton
        onClick={() => !isDisabled && onSelect(folder.id)}
        disabled={isDisabled}
        sx={{
          pl: 2 + level * 2,
          py: 0.75,
          borderRadius: 1,
          bgcolor: isSelected ? alpha('#2563eb', 0.08) : 'transparent',
          '&:hover': {
            bgcolor: isSelected ? alpha('#2563eb', 0.12) : 'grey.50',
          },
        }}
      >
        {hasChildren ? (
          <Box
            onClick={(e) => {
              e.stopPropagation();
              onToggle(folder.id);
            }}
            sx={{ mr: 0.5, display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          >
            {isExpanded ? (
              <ExpandMoreIcon sx={{ fontSize: 20, color: 'grey.500' }} />
            ) : (
              <ChevronRightIcon sx={{ fontSize: 20, color: 'grey.500' }} />
            )}
          </Box>
        ) : (
          <Box sx={{ width: 24, mr: 0.5 }} />
        )}
        <ListItemIcon sx={{ minWidth: 36 }}>
          {isExpanded && hasChildren ? (
            <FolderOpenIcon sx={{ color: folder.color || '#2563eb' }} />
          ) : (
            <FolderIcon sx={{ color: folder.color || '#2563eb' }} />
          )}
        </ListItemIcon>
        <ListItemText
          primary={folder.name}
          primaryTypographyProps={{
            variant: 'body2',
            fontWeight: isSelected ? 600 : 400,
            color: isDisabled ? 'text.disabled' : 'text.primary',
          }}
        />
      </ListItemButton>

      {hasChildren && (
        <Collapse in={isExpanded} timeout="auto">
          {folder.children.map((child) => (
            <FolderTreeItem
              key={child.id}
              folder={child}
              level={level + 1}
              selectedId={selectedId}
              expandedIds={expandedIds}
              disabledId={disabledId}
              onSelect={onSelect}
              onToggle={onToggle}
            />
          ))}
        </Collapse>
      )}
    </>
  );
};

const MoveDialog: React.FC<MoveDialogProps> = ({
  open,
  onClose,
  itemType,
  itemId,
  itemName,
  currentFolderId,
}) => {
  const { t } = useTranslation();
  const { moveFolder, refreshContents } = useFileManager();

  const [folderTree, setFolderTree] = useState<FolderTree[]>([]);
  const [loading, setLoading] = useState(false);
  const [moving, setMoving] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState<number | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<number | null>>(new Set([null]));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      loadFolderTree();
      setSelectedFolderId(null);
      setError(null);
    }
  }, [open]);

  const loadFolderTree = async () => {
    setLoading(true);
    try {
      const tree = await fileService.getFolderTree(undefined, 10);
      setFolderTree(tree);
    } catch (err) {
      console.error('Failed to load folder tree:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (id: number | null) => {
    setExpandedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const handleMove = async () => {
    if (selectedFolderId === currentFolderId) {
      setError(t('fileManager.move.sameFolder', 'Le fichier est deja dans ce dossier'));
      return;
    }

    setMoving(true);
    setError(null);

    try {
      if (itemType === 'folder') {
        await moveFolder(itemId, selectedFolderId);
      } else {
        await fileService.moveDocument(itemId, selectedFolderId);
      }
      await refreshContents();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.detail || t('fileManager.move.error', 'Erreur lors du deplacement'));
    } finally {
      setMoving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, maxHeight: '80vh' },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <DriveFileMoveIcon color="primary" />
        {t('fileManager.move.title', 'Deplacer')} "{itemName}"
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('fileManager.move.selectDestination', 'Selectionnez le dossier de destination')}
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'grey.200',
              borderRadius: 2,
              maxHeight: 400,
              overflow: 'auto',
            }}
          >
            <List dense disablePadding>
              {/* Root folder option */}
              <ListItemButton
                onClick={() => setSelectedFolderId(null)}
                sx={{
                  py: 1,
                  borderRadius: 1,
                  bgcolor: selectedFolderId === null ? alpha('#2563eb', 0.08) : 'transparent',
                  '&:hover': {
                    bgcolor: selectedFolderId === null ? alpha('#2563eb', 0.12) : 'grey.50',
                  },
                }}
              >
                <Box
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggle(null);
                  }}
                  sx={{ mr: 0.5, display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                >
                  {expandedIds.has(null) ? (
                    <ExpandMoreIcon sx={{ fontSize: 20, color: 'grey.500' }} />
                  ) : (
                    <ChevronRightIcon sx={{ fontSize: 20, color: 'grey.500' }} />
                  )}
                </Box>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <HomeIcon sx={{ color: '#2563eb' }} />
                </ListItemIcon>
                <ListItemText
                  primary={t('fileManager.move.rootFolder', 'Mon espace')}
                  primaryTypographyProps={{
                    variant: 'body2',
                    fontWeight: selectedFolderId === null ? 600 : 400,
                  }}
                />
              </ListItemButton>

              {/* Folder tree */}
              <Collapse in={expandedIds.has(null)} timeout="auto">
                {folderTree.map((folder) => (
                  <FolderTreeItem
                    key={folder.id}
                    folder={folder}
                    level={1}
                    selectedId={selectedFolderId}
                    expandedIds={expandedIds}
                    disabledId={itemType === 'folder' ? itemId : undefined}
                    onSelect={setSelectedFolderId}
                    onToggle={handleToggle}
                  />
                ))}
              </Collapse>
            </List>
          </Box>
        )}

        {error && (
          <Typography color="error" variant="body2" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={moving}>
          {t('common.cancel', 'Annuler')}
        </Button>
        <Button
          variant="contained"
          onClick={handleMove}
          disabled={moving || loading}
          startIcon={moving ? <CircularProgress size={16} /> : <DriveFileMoveIcon />}
        >
          {t('fileManager.move.move', 'Deplacer')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MoveDialog;
